import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { API_BASE_URL } from "./config";

const CreateBenefit = () => {
  const [formData, setFormData] = useState({
    benefitId: "",
    name: "",
    description: "",
    accessUrl: ""
  });
  const [bulletPoints, setBulletPoints] = useState([""]);
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const navigate = useNavigate();

  const checkAdminStatus = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API_BASE_URL}/api/admin/check`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!res.data.isAdmin) {
        navigate("/home");
      }
    } catch (err) {
      navigate("/home");
    }
  }, [navigate]);

  useEffect(() => {
    checkAdminStatus();
  }, [checkAdminStatus]);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleBulletPointChange = (index, value) => {
    const newBulletPoints = [...bulletPoints];
    newBulletPoints[index] = value;
    setBulletPoints(newBulletPoints);
  };

  const addBulletPoint = () => {
    setBulletPoints([...bulletPoints, ""]);
  };

  const removeBulletPoint = (index) => {
    const newBulletPoints = bulletPoints.filter((_, i) => i !== index);
    setBulletPoints(newBulletPoints);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!image) {
      setError("Please select an image");
      return;
    }

    const formDataToSend = new FormData();
    formDataToSend.append("benefitId", formData.benefitId);
    formDataToSend.append("name", formData.name);
    formDataToSend.append("description", formData.description);
    formDataToSend.append("accessUrl", formData.accessUrl);
    formDataToSend.append("image", image);
    
    // Filter out empty bullet points
    const filteredBulletPoints = bulletPoints.filter(point => point.trim() !== "");
    formDataToSend.append("bulletPoints", JSON.stringify(filteredBulletPoints));

    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API_BASE_URL}/api/benefits`, formDataToSend, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });

      setSuccess("Benefit created successfully!");
      setTimeout(() => {
        navigate("/admin");
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to create benefit");
    }
  };

  return (
    <div className="container mt-4">
      <h2>Create New Benefit</h2>
      
      <div className="row">
        <div className="col-md-8">
          <form onSubmit={handleSubmit}>
            <div className="mb-3">
              <label className="form-label">Benefit ID *</label>
              <input
                type="text"
                className="form-control"
                name="benefitId"
                value={formData.benefitId}
                onChange={handleInputChange}
                required
                placeholder="e.g., MEDICARE-2024"
              />
            </div>

            <div className="mb-3">
              <label className="form-label">Benefit Name *</label>
              <input
                type="text"
                className="form-control"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                placeholder="e.g., Medicare Advantage Plan"
              />
            </div>

            <div className="mb-3">
              <label className="form-label">Benefit Image *</label>
              <input
                type="file"
                className="form-control"
                accept="image/*"
                onChange={handleImageChange}
                required
              />
              <div className="form-text">
                <strong>Image Guidelines:</strong>
                <ul>
                  <li>Recommended size: 800x450 pixels (16:9 aspect ratio)</li>
                  <li>Maximum file size: 5MB</li>
                  <li>Supported formats: JPG, PNG, GIF</li>
                  <li>The image will be automatically scaled to fit the feed width</li>
                  <li>Use high-quality images with clear visibility</li>
                </ul>
              </div>
              {imagePreview && (
                <div className="mt-3">
                  <p>Preview:</p>
                  <img 
                    src={imagePreview} 
                    alt="Preview" 
                    style={{ 
                      maxWidth: "100%", 
                      height: "auto",
                      maxHeight: "300px",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      padding: "5px"
                    }}
                  />
                </div>
              )}
            </div>

            <div className="mb-3">
              <label className="form-label">Description *</label>
              <textarea
                className="form-control"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                required
                rows="4"
                placeholder="Enter a detailed description of the benefit..."
              />
            </div>

            <div className="mb-3">
              <label className="form-label">Bullet Points</label>
              {bulletPoints.map((point, index) => (
                <div key={index} className="input-group mb-2">
                  <input
                    type="text"
                    className="form-control"
                    value={point}
                    onChange={(e) => handleBulletPointChange(index, e.target.value)}
                    placeholder={`Bullet point ${index + 1}`}
                  />
                  {bulletPoints.length > 1 && (
                    <button
                      type="button"
                      className="btn btn-outline-danger"
                      onClick={() => removeBulletPoint(index)}
                    >
                      Remove
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={addBulletPoint}
              >
                + Add Bullet Point
              </button>
            </div>

            <div className="mb-3">
              <label className="form-label">Access Benefit URL *</label>
              <input
                type="url"
                className="form-control"
                name="accessUrl"
                value={formData.accessUrl}
                onChange={handleInputChange}
                required
                placeholder="https://example.com/benefit-signup"
              />
              <div className="form-text">
                This URL will be used for the "Access Benefit" button
              </div>
            </div>

            {error && <div className="alert alert-danger">{error}</div>}
            {success && <div className="alert alert-success">{success}</div>}

            <div className="mb-3">
              <button type="submit" className="btn btn-primary me-2">
                Create Benefit
              </button>
              <button 
                type="button" 
                className="btn btn-secondary"
                onClick={() => navigate("/admin")}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>

        <div className="col-md-4">
          <div className="card">
            <div className="card-body">
              <h5 className="card-title">Tips for Creating Benefits</h5>
              <ul className="small">
                <li>Use clear, descriptive Benefit IDs for easy management</li>
                <li>Choose engaging images that represent the benefit well</li>
                <li>Write concise but informative descriptions</li>
                <li>Use bullet points to highlight key features</li>
                <li>Ensure the access URL is correct and functional</li>
                <li>Test the benefit display after creation</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateBenefit;